/**
 * rudderStackEvents.ts — NEW, sheet-aligned analytics (CleverTap tracking).
 * ---------------------------------------------------------------------------
 * INTENTIONALLY DECOUPLED from the legacy analytics (`tracking.ts` /
 * `trackingEvents.ts`). This module:
 *   - has its OWN base-property builder and its OWN safe sender,
 *   - does NOT import anything from the legacy tracking modules,
 *   - reuses the SAME already-loaded RudderStack SDK instance
 *     (`window.rudderanalytics`) → same write key, same data plane, same source
 *     that `uniConfig/scripts/RudderStack.tsx` initializes. No second init.
 *
 * REMOVAL PLAN: to fully remove this feature later, delete THIS FILE and
 * uncomment the `[RUDDERSTACK-OLD ...]` blocks in the few instrumented widgets
 * (search that marker). Nothing else references this module.
 *
 * Event names + property names below follow the tracking sheet verbatim.
 * ---------------------------------------------------------------------------
 */

import type { RefObject } from "react";

// Reading the Redux store is shared app infra, NOT the legacy tracking system
// — so this stays decoupled from `tracking.ts`.
import { store } from "@/store/store";
// [GAP 1 — Atlas parity, additive] host→tenant-key resolver (already in this branch).
import { resolveTenantKeyFromHost } from "@/lib/tenantKey";

/* ─────────────────────────── low-level sender ─────────────────────────── */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Props = Record<string, any>;

/**
 * Routes where events must NOT fire. The CMS editor/preview
 * (`/admin/pages/:id/edit`, `/admin/pages/:id/preview`, and any other `/admin/*`
 * screen) render the SAME widgets, so their interactions would pollute real
 * user analytics. Gate ALL events in one place. Extend if other non-public
 * routes appear.
 */
const isNonTrackableRoute = (): boolean => {
  if (typeof window === "undefined") return true;
  const path = window.location.pathname;
  return path === "/admin" || path.startsWith("/admin/");
};

/**
 * Page-level MASTER switch (globalslice.trackingEnabled, hydrated from
 * page.tracking_enabled by NewPage). Only an explicit `false` disables — NULL /
 * undefined / true keep tracking on, so already-live pages need no toggle. When
 * off, EVERY event (page_open, widget_impression, cta_click, lead events, …) is
 * suppressed here in one place.
 */
const isPageTrackingDisabled = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = (store.getState() as any)?.globalslice;
    return g?.trackingEnabled === false;
  } catch {
    return false;
  }
};

/**
 * Dev-only detailed console logging of every tracking attempt. Gated on
 * NODE_ENV so the whole thing (and its console calls) is dead-code-eliminated in
 * production builds — zero cost, zero leaked logs live.
 *
 * Logs BOTH sent events AND skips with the reason (admin/preview route, or the
 * lazy SDK not being ready yet) so "why isn't my event firing?" is answerable
 * from the console alone. Each entry is a collapsed group with the event name, a
 * one-line summary of the key fields, the full merged payload, and a table.
 */
const IS_DEV = process.env.NODE_ENV === "development";

type TrackStatus =
  | "sent"
  | "skipped: admin/preview route"
  | "skipped: page tracking disabled"
  | "buffered: SDK not ready (will flush on load)"
  | "skipped: SDK not ready";

/** Pull the most identifying field out of a payload for the log headline. */
const headlineOf = (props: Props): string => {
  const parts: string[] = [];
  for (const k of [
    "widget_name",
    "cta_name",
    "cta_text",
    "popup_name",
    "component_name",
    "form_name",
    "field_name",
  ]) {
    if (props[k]) parts.push(`${k}=${props[k]}`);
  }
  if (props.widget_order !== undefined && props.widget_order !== "") {
    parts.push(`order=${props.widget_order}`);
  }
  return parts.join("  ");
};

const devLogEvent = (status: TrackStatus, event: string, props: Props): void => {
  if (!IS_DEV || typeof window === "undefined") return;
  const sent = status === "sent";
  const color = sent ? "#16a34a" : status.includes("SDK") ? "#f59e0b" : "#9ca3af";
  const headline = headlineOf(props);
  /* eslint-disable no-console */
  console.groupCollapsed(
    `%c[track]%c ${event}%c ${sent ? "✓ sent" : "⊘ " + status}%c${headline ? "  —  " + headline : ""}`,
    `color:${color};font-weight:bold`,
    "color:inherit;font-weight:bold",
    `color:${color}`,
    "color:#6b7280",
  );
  console.log("event   :", event);
  console.log("status  :", sent ? "sent to RudderStack (window.rudderanalytics.track)" : status);
  console.log("time    :", new Date().toISOString());
  console.log("payload :", props);
  try {
    console.table(props);
  } catch {
    /* console.table unavailable — payload already logged above */
  }
  console.groupEnd();
  /* eslint-enable no-console */
};

/**
 * The RudderStack SDK is loaded lazily by <DeferUntilInteraction/> — it mounts only after the
 * FIRST user interaction (scroll/pointer/key/touch) or a ~4.5s backstop, so `window.rudderanalytics`
 * does NOT exist at first paint. An ABOVE-THE-FOLD widget fires its impression on load, before any
 * of that, so the SDK isn't there yet. Rather than DROP that event (which is how above-the-fold
 * impressions like About3 / CourseOutcome2 silently went missing — the observer only re-checks on a
 * scroll, and an already-visible top section never gets one), we BUFFER it and flush the moment the
 * SDK appears. Everything the buffer holds already passed the route + page-master gates.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rudderReady = (): any => {
  if (typeof window === "undefined") return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ra = (window as any).rudderanalytics;
  return ra && typeof ra.track === "function" ? ra : null;
};

const MAX_PENDING = 100; // ceiling so a never-loading SDK (no write key) can't grow this unbounded
const MAX_FLUSH_ATTEMPTS = 50; // ~20s at 400ms — the 4.5s interaction backstop mounts it well before
let pendingEvents: Array<{ event: string; props: Props }> = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let flushAttempts = 0;

const flushPending = (ra: { track: (e: string, p: Props) => void }): void => {
  const queued = pendingEvents;
  pendingEvents = [];
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  for (const { event, props } of queued) {
    ra.track(event, props);
    devLogEvent("sent", event, props);
  }
};

const scheduleFlush = (): void => {
  if (flushTimer !== null || typeof window === "undefined") return;
  flushAttempts = 0;
  flushTimer = setInterval(() => {
    flushAttempts += 1;
    const ra = rudderReady();
    if (ra) {
      flushPending(ra);
    } else if (flushAttempts >= MAX_FLUSH_ATTEMPTS) {
      // SDK never appeared (e.g. no write key) — stop polling and drop the buffer.
      pendingEvents = [];
      if (flushTimer) {
        clearInterval(flushTimer);
        flushTimer = null;
      }
    }
  }, 400);
};

/**
 * Safe sender. Never throws — a widget calling this must never break because tracking is off.
 *
 * Returns `true` when the event was sent OR buffered for send (it will go out once the lazy SDK
 * mounts); `false` only for a real skip (admin/preview route, page-master switch off).
 */
const sendEvent = (event: string, props: Props = {}): boolean => {
  if (typeof window === "undefined") return false;
  if (isNonTrackableRoute()) {
    devLogEvent("skipped: admin/preview route", event, props);
    return false; // skip CMS editor/preview
  }
  if (isPageTrackingDisabled()) {
    devLogEvent("skipped: page tracking disabled", event, props);
    return false; // page-level master switch is off
  }
  const merged = { ...getBaseProperties(), ...props };
  const ra = rudderReady();
  if (!ra) {
    // SDK not mounted yet (deferred until interaction) — buffer, don't drop.
    if (pendingEvents.length >= MAX_PENDING) pendingEvents.shift();
    pendingEvents.push({ event, props: merged });
    scheduleFlush();
    devLogEvent("buffered: SDK not ready (will flush on load)", event, merged);
    return true;
  }
  ra.track(event, merged);
  devLogEvent("sent", event, merged);
  return true;
};

/* ─────────────────────────── base properties ──────────────────────────── */
// Shared properties every event inherits.
//
// TRIMMED: keys that RudderStack already provides in its envelope / `context`
// are NOT re-sent (avoids duplication + payload bloat). What stays here =
// business-specific fields RudderStack doesn't provide as-is.
//
// REMOVED as redundant (RudderStack provides them):
//   user_id      → top-level `userId` (if identified) + `anonymousId`
//   timestamp    → `originalTimestamp` / `sentAt` / `timestamp`
//   page_url     → `context.page.url`
//   ip_address   → `request_ip` (server-captured)
//   device_os    → `context.os`
//   device_name  → `context.userAgent`
//   browser      → `context.userAgent` (+ `context.userAgentData`)
//   source       → `context.campaign.source`
//   media        → `context.campaign.medium`
//   campaign     → `context.campaign.name`
//   utm_source   → `context.campaign.source`
//   utm_media    → `context.campaign.medium`
//   utm_campaign → `context.campaign.name`
//   session_id   → `context.sessionId` (RudderStack auto session tracking)

const getBaseProperties = (): Props => {
  if (typeof window === "undefined") return {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = (store.getState() as any)?.globalslice ?? {};
  // university.name, read from the `university` table and hydrated into Redux by
  // ClientWrapper (via the tenant-routed university-settings response). The DB value MUST
  // win over the env: NEXT_PUBLIC_UNIVERSITY_NAME is inlined at build time, so in
  // multi-tenant a single build would otherwise label EVERY tenant's events with the same
  // university. Env remains the fallback for standalone and for events that fire before
  // the store hydrates. (The old `g?.universityName` read was dead — no such key exists.)
  const university =
    (g?.university?.name || process.env.NEXT_PUBLIC_UNIVERSITY_NAME || "")
      .toString()
      .toLowerCase();
  const isMobile = window.innerWidth < 800;
  // [GAP 1 — Atlas parity, ADDITIVE] host-resolved tenant key + Chromium client hint.
  const tenantKey = (resolveTenantKeyFromHost(window.location.host) || "").toLowerCase();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { userAgentData } = window.navigator as any;

  return {
    // [GAP 1 additive] Host-resolved tenant key (e.g. "onlineatlas_in"). Kept
    // SEPARATE from `university` (display name below) so existing values don't
    // change — no segment break. Set `university` = tenantKey later for full Atlas
    // parity if desired.
    business_entity: tenantKey,
    // Derived business flag — RudderStack gives raw screen/userAgent, not a
    // simple mobile/desktop flag. Atlas taxonomy values ("mobile_web"/"desktop_web").
    device_type: isMobile ? "mobile_web" : "desktop_web",
    // [GAP 1 additive] sheet/legacy taxonomy value.
    source_identifier: isMobile ? "mobile" : "web",
    // [GAP 1 additive] Chromium-only client hint; "" on Safari/Firefox.
    platform_os: userAgentData?.platform || "",
    // Geo from app Redux. NOTE: CleverTap can also derive geo from `request_ip`.
    city: g?.city || "",
    state: g?.state || "",
    // Business page category (home_page/program_page/…). Not in RudderStack.
    page_type: g?.pageCategory || "",
    university,
  };
};

/* ───────────────────────────── events ─────────────────────────────────── */

/**
 * 2 · widget_impression — fires EACH TIME the widget crosses INTO "at least 50%
 * visible on the screen". Scroll it out of view and back and it fires again; a
 * section can produce any number of impressions in one page view.
 *
 * "50% visible" = ≥50% of the widget is in view OR (for widgets TALLER than the
 * viewport) the widget covers ≥50% of the viewport. Using `min(widgetH,
 * viewportH)` as the denominator collapses both cases into one rule, so a very
 * tall widget (e.g. a long stacked card grid) still fires — a plain
 * `threshold: 0.5` would never trigger for it because its own visible ratio
 * can't reach 0.5.
 *
 * RISING EDGE, not continuous: we fire on the transition from <50% → ≥50%, using
 * the `visible` flag, so a single scroll-in is ONE event, not one per observer
 * callback. Falling below 50% re-arms it for the next entry.
 *
 * ABOVE-THE-FOLD: a top section is ≥50% visible on load, before the lazy SDK
 * (DeferUntilInteraction) mounts. `sendEvent` BUFFERS that first impression and
 * flushes it when the SDK appears, so it is not lost even though the observer
 * never fires again without a scroll. See sendEvent's buffer.
 */
export const trackWidgetImpression = (
  ref: RefObject<HTMLElement | null>,
  params: { widget_name: string; widget_order?: number | string },
): (() => void) => {
  if (typeof window === "undefined" || !ref.current) return () => {};
  const el = ref.current;
  let visible = false; // is the widget currently ≥50% visible?
  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;
      const visibleH = entry.intersectionRect.height;
      const widgetH = entry.boundingClientRect.height;
      const viewportH = entry.rootBounds?.height ?? window.innerHeight;
      const denom = Math.min(widgetH, viewportH);
      const isNowVisible = denom > 0 && visibleH / denom >= 0.5;
      // Rising edge: fire each time it crosses INTO ≥50% visible (out-and-back
      // re-fires). Buffering in sendEvent covers the lazy-SDK / above-the-fold case.
      if (isNowVisible && !visible) {
        sendEvent("widget_impression", {
          widget_name: params.widget_name,
          widget_order: params.widget_order ?? "",
        });
      }
      visible = isNowVisible;
    },
    // Fine-grained thresholds so the callback keeps running as the widget
    // scrolls, even when its own visible ratio can never reach 0.5.
    { threshold: Array.from({ length: 21 }, (_, i) => i / 20) },
  );
  observer.observe(el);
  // Return a cleanup so the caller disconnects on unmount. CRITICAL: React
  // StrictMode (dev) runs effects twice; without cleanup two observers would be
  // left observing and BOTH would fire on entry.
  return () => observer.disconnect();
};

/** 3 · cta_click */
export const trackCtaClick = (params: {
  widget_name: string;
  widget_order?: number | string;
  cta_name: string;
  cta_text: string;
}): void => {
  sendEvent("cta_click", {
    widget_name: params.widget_name,
    widget_order: params.widget_order ?? "",
    cta_name: params.cta_name,
    cta_text: params.cta_text,
  });
};

/** 4 · popup_open */
export const trackPopupOpen = (params: { popup_name: string }): void => {
  sendEvent("popup_open", { popup_name: params.popup_name });
};

/** 5 · cross_click */
export const trackCrossClick = (params: { component_name: string }): void => {
  sendEvent("cross_click", { component_name: params.component_name });
};

/** 1 · page_open — page-level; call once per page load. */
export const trackPageOpen = (): void => {
  sendEvent("page_open");
};

/** 14 · tab_switched — page-level; fire on visibility change / blur. */
export const trackTabSwitched = (): void => {
  sendEvent("tab_switched");
};

/* ── lead-form field events (6–13) ──────────────────────────────────────── */

/** 12 · leadform_fillstart — user focused/entered a field, about to fill it. */
export const trackLeadformFillstart = (p: {
  form_name: string;
  field_name: string;
}): void =>
  void sendEvent("leadform_fillstart", {
    form_name: p.form_name,
    field_name: p.field_name,
  });

export const trackLeadformMobile = (p: { form_name: string }): void =>
  void sendEvent("leadform_mobile", { form_name: p.form_name });

export const trackLeadName = (p: { form_name: string }): void =>
  void sendEvent("lead_name", { form_name: p.form_name });

export const trackLeadformEmail = (p: { form_name: string }): void =>
  void sendEvent("leadform_email", { form_name: p.form_name });

export const trackLeadHq = (p: { form_name: string; selected_qualification: string }): void =>
  void sendEvent("lead_hq", { form_name: p.form_name, selected_qualification: p.selected_qualification });

export const trackLeadAoi = (p: { form_name: string; selected_program: string }): void =>
  void sendEvent("lead_aoi", { form_name: p.form_name, selected_program: p.selected_program });

export const trackLeadformError = (p: {
  form_name: string;
  field_name: string;
  error_message: string;
  error_code?: string | number;
}): void =>
  void sendEvent("leadform_error", {
    form_name: p.form_name,
    field_name: p.field_name,
    error_message: p.error_message,
    error_code: p.error_code ?? "",
  });

export const trackLeadSubmit = (p: {
  form_name: string;
  lead_id?: string | number;
}): void =>
  void sendEvent("lead_submit", { form_name: p.form_name, lead_id: p.lead_id ?? "" });

/**
 * lead_submit_fail — a submission attempt (OTP send or OTP verify) failed. `reason`
 * is the human-readable API/OTP message so drop-off can be diagnosed. Owns
 * submission/API failures; pure field validation stays on leadform_error.
 */
export const trackLeadSubmitFail = (p: {
  form_name: string;
  reason: string;
  error_code?: string | number;
}): void =>
  void sendEvent("lead_submit_fail", {
    form_name: p.form_name,
    reason: p.reason,
    error_code: p.error_code ?? "",
  });

/** 13 · chat_click */
export const trackChatClick = (p: { chat_type: string }): void =>
  void sendEvent("chat_click", { chat_type: p.chat_type });

/* ───────────────────────────── identity ───────────────────────────────── */
// For CleverTap to attach events to a real profile, identify with email/phone.
// DOUBT: confirm the identity key CleverTap expects (email vs phone vs custom).
export const identifyUser = (userId: string, traits: Props = {}): void => {
  if (typeof window === "undefined") return;
  const idProps: Props = { user_id: userId, ...traits };
  if (isNonTrackableRoute()) {
    devLogEvent("skipped: admin/preview route", "identify", idProps);
    return; // skip CMS editor/preview
  }
  if (isPageTrackingDisabled()) {
    devLogEvent("skipped: page tracking disabled", "identify", idProps);
    return; // page-level master switch is off
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ra = (window as any).rudderanalytics;
  if (!ra || typeof ra.identify !== "function") {
    devLogEvent("skipped: SDK not ready", "identify", idProps);
    return;
  }
  ra.identify(userId, traits);
  devLogEvent("sent", "identify", idProps);
};
